import { FlexView, Row } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';
import { Table, TableCell } from 'comps';
import React, { useCallback, useEffect, useState } from 'react';
import { TableRow } from 'semantic-ui-react';
import styled from 'styled-components';

import COLORS from '../../../constants/colors';
import { useGetAllEvalSpecialistCapacity } from 'actions/eval-reporting';
import SlotDetail from './slot-detail';
import {
  ClickableNumber,
  ErrorLine,
  FilterRow,
  MonoText,
  PageHeader,
  RangePicker,
  RegionPicker,
  TimezonePicker,
} from '../common/filters';
import {
  CAPACITY_SLOT_STATUSES,
  IST_ZONE,
  RANGE_TYPES,
  currentMonthValue,
  rangeError,
  rangeQuery,
} from '../common/constants';

const Container = styled(FlexView)`
  padding: 0 16px;
  width: 100%;
`;
const TableWrapper = styled(Row)`
  margin-top: 16px;
  width: 100%;
`;
const TotalsRow = styled(TableRow)`
  background: ${COLORS.WILD_SAND};
  font-weight: 600;
`;

const utilisation = (filled, given) => {
  if (!given) return '-';

  return `${Math.round((filled / given) * 100)}%`;
};

const Capacity = () => {
  const [range, setRange] = useState({
    rangeType: RANGE_TYPES.week,
    month: currentMonthValue,
    startDate: '',
    endDate: '',
  });
  const [zone, setZone] = useState(IST_ZONE);
  const [region, setRegion] = useState(null);
  const [slotTarget, setSlotTarget] = useState(null);

  const {
    data,
    getAll: getCapacity,
    isProcessing: isLoading,
    error: capacityError,
  } = useGetAllEvalSpecialistCapacity();

  const isRangeInvalid =
    range.rangeType === RANGE_TYPES.custom &&
    Boolean(rangeError(range.startDate, range.endDate));

  const fetchCapacity = useCallback(() => {
    if (isRangeInvalid) return;
    getCapacity({ ...rangeQuery(range, zone), ...(region ? { region } : {}) });
  }, [getCapacity, range, zone, region, isRangeInvalid]);

  useEffect(() => {
    fetchCapacity();
  }, [fetchCapacity]);

  const rows = data?.specialists || [];
  const totals = data?.totals || {};

  const resolvedMonth = data?.month || range.month;

  const openSlots = useCallback(
    (row, status) => {
      setSlotTarget({ row, status, month: resolvedMonth, zone });
    },
    [resolvedMonth, zone],
  );

  return (
    <Container>
      <PageHeader title="Specialist Capacity" />
      <FilterRow justifyContent="space-between" alignItems="center">
        <RangePicker value={range} onChange={setRange} />
        <RegionPicker value={region} onChange={setRegion} />
        <TimezonePicker value={zone} onChange={setZone} region={region} />
        <Text variant="body3" color={COLORS.GRAY}>
          {data?.start_date && data?.end_date
            ? `${data.start_date} to ${data.end_date}. `
            : ''}
          Given excludes paused time. Click a filled or available count to see the slots.
        </Text>
      </FilterRow>

      <ErrorLine error={capacityError} />

      <TableWrapper>
        <Table
          headerCell={[
            { title: 'Specialist' },
            { title: 'Specialist ID' },
            { title: 'Given' },
            { title: 'Filled' },
            { title: 'Available' },
            { title: 'Utilisation' },
          ]}
          dataLength={rows.length}
          havePagination
          showInfo={false}
          showLoader={isLoading}
          flexBasis="100%"
          noDataMessage="No capacity to report for this window."
          scrollBodyHeight={isLoading || !rows.length ? '532px' : 'calc(100vh - 210px)'}
        >
          {rows.map(row => (
            <TableRow key={row.teacher_id}>
              <TableCell width={4} textTransform="none">
                {row.teacher_name || '-'}
              </TableCell>
              <TableCell width={4} textTransform="none">
                <MonoText>{row.teacher_id}</MonoText>
              </TableCell>
              <TableCell width={2}>
                <MonoText>{row.given ?? 0}</MonoText>
              </TableCell>
              <TableCell width={2}>
                <ClickableNumber
                  disabled={!row.filled}
                  onClick={() =>
                    row.filled && openSlots(row, CAPACITY_SLOT_STATUSES.filled)
                  }
                >
                  {row.filled ?? 0}
                </ClickableNumber>
              </TableCell>
              <TableCell width={2}>
                <ClickableNumber
                  disabled={!row.available}
                  onClick={() =>
                    row.available && openSlots(row, CAPACITY_SLOT_STATUSES.available)
                  }
                >
                  {row.available ?? 0}
                </ClickableNumber>
              </TableCell>
              <TableCell width={2}>
                <MonoText>{utilisation(row.filled, row.given)}</MonoText>
              </TableCell>
            </TableRow>
          ))}
          {rows.length > 0 && (
            <TotalsRow>
              <TableCell width={4} textTransform="none">
                All specialists
              </TableCell>
              <TableCell width={4} textTransform="none">
                {rows.length} specialist{rows.length === 1 ? '' : 's'}
              </TableCell>
              <TableCell width={2}>
                <MonoText>{totals.given ?? 0}</MonoText>
              </TableCell>
              <TableCell width={2}>
                <MonoText>{totals.filled ?? 0}</MonoText>
              </TableCell>
              <TableCell width={2}>
                <MonoText>{totals.available ?? 0}</MonoText>
              </TableCell>
              <TableCell width={2}>
                <MonoText>{utilisation(totals.filled, totals.given)}</MonoText>
              </TableCell>
            </TotalsRow>
          )}
        </Table>
      </TableWrapper>

      {slotTarget && (
        <SlotDetail target={slotTarget} closeModal={() => setSlotTarget(null)} />
      )}
    </Container>
  );
};

export default Capacity;
